// ========================================================
// FAST HIT Website — Route Guards
// ========================================================

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoadingScreen } from '../components/common/LoadingScreen';

type ProtectedRouteProps = {
  children: React.ReactNode;
  /** Optional: require specific roles (e.g., scorer for scoring pages) */
  requiredRole?: string;
};

/**
 * Wraps routes that require authentication.
 * - Loading → loading screen
 * - Not authenticated → redirect to /login with return URL
 * - Authenticated → render children
 */
export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { isLoading, isAuthenticated, profile } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingScreen message="Checking authentication..." />;
  }

  if (!isAuthenticated) {
    // Validate return route to prevent open redirects (prevent protocol-relative URL exploits like //evil.com)
    const returnTo =
      location.pathname.startsWith('/') && !location.pathname.startsWith('//')
        ? location.pathname
        : '/';
    return <Navigate to="/login" state={{ from: returnTo }} replace />;
  }

  if (requiredRole) {
    const userRoles = profile?.roles || ['player'];
    const hasRole = userRoles.includes(requiredRole) || userRoles.includes('admin');
    if (!hasRole) {
      return (
        <div className="container py-16 text-center">
          <h2 className="text-xl font-bold mb-2">Access Denied</h2>
          <p className="text-secondary text-sm mb-6">
            You do not have the required permissions ({requiredRole}) to access this console.
          </p>
          <Navigate to="/app" replace />
        </div>
      );
    }
  }

  return <>{children}</>;
}

/**
 * Wraps routes that should redirect authenticated users away
 * (e.g., login page → redirect to /app if already signed in)
 */
export function GuestRoute({ children }: { children: React.ReactNode }) {
  const { isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return <LoadingScreen message="Loading..." />;
  }

  if (isAuthenticated) {
    return <Navigate to="/app" replace />;
  }

  return <>{children}</>;
}
